import { EntityAppearanceType } from '@/api/generated/api.enum';
import { computed, ref, shallowRef } from 'vue';
import { useObjectUrl } from '@vueuse/core';
import {
  DEFAULT_APPEARANCE_COLOR,
  DEFAULT_APPEARANCE_ICON,
  DEFAULT_EMOJI_KEY,
  DEFAULT_EMOJI_UNICODE,
} from '@/core/entity-appearance/appearance.constants';
import type {
  TAppearanceImage,
  TAppearanceTab,
  TEntityAppearance,
  TEmojiKey,
} from '@/core/entity-appearance/entityAppearance.types';
import type { EIcon } from 'common/enum/misc.enum';

export const useAppearanceImageUrl = (getSource: () => File | string | undefined) => {
  const file = computed(() => {
    const source = getSource();
    return typeof source === 'string' ? undefined : source;
  });
  const objectUrl = useObjectUrl(file);
  return computed(() => {
    const source = getSource();
    return typeof source === 'string' ? source : objectUrl.value;
  });
};

export const useAppearanceDraft = (getValue: () => TEntityAppearance, commit: (value: TEntityAppearance) => void) => {
  const isOpen = ref(false);
  const draft = shallowRef<TEntityAppearance>(getValue());
  const activeTab = ref<TAppearanceTab>(draft.value.type);
  const backgroundColor = ref(DEFAULT_APPEARANCE_COLOR);
  let emojiKey = DEFAULT_EMOJI_KEY;
  let emojiUnicode = DEFAULT_EMOJI_UNICODE;
  let iconKey = DEFAULT_APPEARANCE_ICON;
  const image = shallowRef<TAppearanceImage | null>(null);

  const open = (): void => {
    const value = getValue();
    draft.value = { ...value };
    activeTab.value = value.type;
    backgroundColor.value =
      value.type === EntityAppearanceType.image ? DEFAULT_APPEARANCE_COLOR : value.backgroundColor;
    emojiUnicode = value.type === EntityAppearanceType.emoji ? value.unicode : DEFAULT_EMOJI_UNICODE;
    emojiKey = value.type === EntityAppearanceType.emoji ? value.key : DEFAULT_EMOJI_KEY;
    iconKey = value.type === EntityAppearanceType.icon ? value.key : DEFAULT_APPEARANCE_ICON;
    image.value = value.type === EntityAppearanceType.image ? value : null;
    isOpen.value = true;
  };

  const selectTab = (tab: TAppearanceTab): void => {
    if (!isOpen.value) return;
    activeTab.value = tab;
    const color = backgroundColor.value;
    switch (tab) {
      case EntityAppearanceType.text:
        draft.value = { type: EntityAppearanceType.text, backgroundColor: color };
        break;
      case EntityAppearanceType.emoji:
        draft.value = {
          type: EntityAppearanceType.emoji,
          backgroundColor: color,
          key: emojiKey,
          unicode: emojiUnicode,
        };
        break;
      case EntityAppearanceType.icon:
        draft.value = { type: EntityAppearanceType.icon, backgroundColor: color, key: iconKey };
        break;
      case EntityAppearanceType.image:
        if (image.value) draft.value = image.value;
        break;
    }
  };

  const selectEmoji = (key: TEmojiKey, unicode: string): void => {
    if (!isOpen.value) return;
    emojiKey = key;
    emojiUnicode = unicode;
    selectTab(EntityAppearanceType.emoji);
  };

  const selectIcon = (key: EIcon): void => {
    if (!isOpen.value) return;
    iconKey = key;
    selectTab(EntityAppearanceType.icon);
  };

  const selectImage = (file: File): void => {
    if (!isOpen.value) return;
    image.value = { type: EntityAppearanceType.image, source: file };
    selectTab(EntityAppearanceType.image);
  };

  const removeImage = (): void => {
    if (!isOpen.value) return;
    image.value = null;
    if (draft.value.type === EntityAppearanceType.image)
      draft.value = { type: EntityAppearanceType.text, backgroundColor: backgroundColor.value };
  };

  const selectBackground = (color: string): void => {
    if (!isOpen.value) return;
    backgroundColor.value = color;
    if (draft.value.type !== EntityAppearanceType.image) draft.value = { ...draft.value, backgroundColor: color };
  };

  const close = (): void => {
    if (!isOpen.value) return;
    isOpen.value = false;
    commit({ ...draft.value });
    // Files are owned by the committed value once the dialog closes.
    image.value = null;
  };

  const preview = computed(() => (isOpen.value ? draft.value : getValue()));
  return {
    activeTab,
    backgroundColor,
    close,
    draft,
    image,
    isOpen,
    open,
    preview,
    removeImage,
    selectBackground,
    selectEmoji,
    selectIcon,
    selectImage,
    selectTab,
  };
};
